// GLSL for the three concentric globe layers + ocean + wind

// ---------- Layer 2: Dynamic Cloud Deck (eyewall + spiral, intensity-driven displacement) ----------
export const cloudVert = /* glsl */ `
  uniform float uTime;
  uniform float uIntensity;   // 0..1
  uniform vec2  uStormUV;     // center of active storm
  uniform float uActive;      // 0/1 whether storm overlay is active
  uniform float uCycle;       // 0 = hurricane (circular), 1 = nor'easter (diagonal)
  varying vec2 vUv;
  varying float vElev;
  varying vec3 vWNormal;

  float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7))) * 43758.5453); }
  float vnoise(vec2 p){
    vec2 i = floor(p), f = fract(p);
    float a = hash(i), b = hash(i+vec2(1.0,0.0)), c = hash(i+vec2(0.0,1.0)), d = hash(i+vec2(1.0,1.0));
    vec2 u = f*f*(3.0-2.0*f);
    return mix(a,b,u.x) + (c-a)*u.y*(1.0-u.x) + (d-b)*u.x*u.y;
  }
  float fbm(vec2 p){ float v=0.0, a=0.5; for(int i=0;i<4;i++){ v+=a*vnoise(p); p*=2.0; a*=0.5; } return v; }

  void main() {
    vUv = uv;
    vWNormal = normalize(mat3(modelMatrix) * normal);
    vec2 d = uv - uStormUV;
    d.x = mod(d.x + 0.5, 1.0) - 0.5;        // wrap longitude
    d *= vec2(3.0, 1.9);                      // compact, realistic storm footprint
    float dist = length(d);
    float ang = atan(d.y, d.x);

    float field;
    if (uCycle < 0.5) {
      float spiral = sin(ang * 5.0 + dist * 34.0 - uTime * 2.2);
      float ring = exp(-pow((dist - 0.06) / 0.05, 2.0));
      float eye  = smoothstep(0.0, 0.03, dist);
      field = uActive * uIntensity * (ring + 0.3 * spiral * exp(-dist * 13.0));
      field *= eye;
    } else {
      float diag = (d.x + d.y);
      float band = exp(-pow(diag / 0.08, 2.0));
      float ripple = sin((d.x - d.y) * 34.0 - uTime * 2.0);
      field = uActive * uIntensity * (band + 0.28 * ripple * exp(-dist * 9.0));
    }

    // billowing volumetric puffiness — fbm turbulence stacks convective cells
    float puff = fbm(uv * 46.0 + vec2(uTime * 0.06, -uTime * 0.04));
    float puff2 = fbm(uv * 95.0 - uTime * 0.03);
    float billow = field * (0.5 + 0.7 * puff + 0.3 * puff2);

    vElev = max(billow, 0.0);
    vec3 pos = position + normal * vElev * 0.075;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  }
`;

export const cloudFrag = /* glsl */ `
  uniform sampler2D uCloudTex;
  uniform vec3 uGold;
  uniform float uIntensity;
  uniform float uMode;     // 0 = realistic (white clouds), 1 = forensic (colored), 2 = thermal
  uniform float uCycle;    // 0 = hurricane (warm), 1 = nor'easter (cold)
  uniform vec3 uSunDir;
  varying vec2 vUv;
  varying float vElev;
  varying vec3 vWNormal;

  void main() {
    vec4 c = texture2D(uCloudTex, vUv);
    float lum = (c.r + c.g + c.b) / 3.0;
    float storm = clamp(vElev * 4.5, 0.0, 1.0);

    if (uMode < 0.5) {
      // Photoreal: brilliant white cloud tops, deep grey shadowing in the troughs
      vec3 grey = vec3(0.42, 0.45, 0.52);
      vec3 white = vec3(1.0);
      vec3 base = mix(grey, white, smoothstep(0.04, 0.6, lum));
      float towers = clamp(vElev * 3.0, 0.0, 1.0);
      vec3 col = mix(base, white, towers);
      float hot = pow(max(dot(normalize(vWNormal), normalize(uSunDir)), 0.0), 6.0);
      col += vec3(1.0, 0.97, 0.9) * hot * 0.5;
      float alpha = clamp(lum * 1.05 + towers * 0.62, 0.0, 1.0) * 0.95;
      gl_FragColor = vec4(col, alpha);
    } else if (uMode < 1.5) {
      // Forensic: colored energy engine
      vec3 col = mix(vec3(1.0), uGold, storm * 0.7);
      float alpha = clamp(lum * 1.1 + storm * 0.9, 0.0, 1.0) * 0.95;
      gl_FragColor = vec4(col, alpha);
    } else {
      // Thermal Core: warm-core RED (hurricane) vs cold-core BLUE (nor'easter), translucent
      vec3 warm = mix(vec3(1.0, 0.92, 0.35), vec3(0.95, 0.08, 0.04), smoothstep(0.12, 1.0, storm));
      vec3 cold = mix(vec3(0.62, 0.85, 1.0), vec3(0.04, 0.12, 0.92), smoothstep(0.12, 1.0, storm));
      vec3 col = uCycle < 0.5 ? warm : cold;
      float alpha = clamp(storm * 0.9 + lum * 0.12, 0.0, 1.0) * 0.6;   // semi-translucent: geography stays visible
      gl_FragColor = vec4(col, alpha);
    }
  }
`;

// ---------- Layer 3: Rayleigh Atmosphere (Fresnel rim, reacts to sun) ----------
export const atmoVert = /* glsl */ `
  varying vec3 vNormal;
  varying vec3 vView;
  void main() {
    vNormal = normalize(mat3(modelMatrix) * normal);
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vView = normalize(cameraPosition - wp.xyz);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const atmoFrag = /* glsl */ `
  uniform vec3 uInner;   // blue
  uniform vec3 uOuter;   // gold
  uniform vec3 uSunDir;
  uniform float uIntensity;
  varying vec3 vNormal;
  varying vec3 vView;
  void main() {
    float rim = pow(1.0 - max(dot(vNormal, vView), 0.0), 3.0);
    float sun = clamp(dot(vNormal, uSunDir) * 0.5 + 0.5, 0.0, 1.0);
    vec3 col = mix(uInner, uOuter, pow(sun, 2.2));
    float a = rim * (0.55 + 0.45 * uIntensity);
    gl_FragColor = vec4(col * (0.6 + rim), a);
  }
`;

// ---------- Ocean shell: wave-height displacement (mesh displacement shader) ----------
export const oceanVert = /* glsl */ `
  uniform float uTime;
  uniform float uIntensity;
  uniform sampler2D uMask;   // specular map: ocean is bright
  varying float vMask;
  varying float vWave;
  varying float vLat;
  void main() {
    float mask = texture2D(uMask, uv).r;
    float w = sin(uv.x * 35.0 + uTime * 2.0) * cos(uv.y * 35.0 - uTime * 1.6);
    w += 0.5 * sin(uv.x * 80.0 - uTime * 3.0);
    float disp = mask * w * (0.0015 + uIntensity * 0.011);
    vMask = mask;
    vWave = w;
    vLat = uv.y;
    vec3 pos = position + normal * disp;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  }
`;

export const oceanFrag = /* glsl */ `
  uniform vec3 uColor;
  uniform float uIntensity;
  uniform float uZoom;      // 0 far .. 1 near -> fade to reveal sea floor
  uniform float uMode;      // 2 = thermal
  uniform float uCycle;     // 0 = hurricane
  varying float vMask;
  varying float vWave;
  varying float vLat;
  void main() {
    if (vMask < 0.35) discard;
    float sheen = 0.5 + 0.5 * clamp(vWave * 0.5 + 0.5, 0.0, 1.0);
    float base = vMask * (0.05 + uIntensity * 0.12) * sheen;
    float a = base * (1.0 - uZoom * 0.85);   // near coastline zoom -> translucent, sea floor shows
    vec3 col = mix(uColor, vec3(0.04, 0.12, 0.22), uZoom) * (0.6 + 0.4 * sheen);

    // Sea Surface Temperature: warm tropical seas (>26C) glow RED in Hurricane thermal view = the fuel source
    if (uMode > 1.5 && uCycle < 0.5) {
      float latC = (vLat - 0.5) * 180.0;
      float warm = smoothstep(30.0, 6.0, abs(latC));
      col = mix(col, vec3(0.92, 0.16, 0.05), warm * 0.75);
      a = max(a, vMask * warm * 0.45 * (1.0 - uZoom * 0.5));
    }
    gl_FragColor = vec4(col, a);
  }
`;

// ---------- Storm surge water (ground level) ----------
export const surgeVert = /* glsl */ `
  uniform float uTime;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    vec3 pos = position;
    pos.z += sin(pos.x * 1.5 + uTime * 2.0) * 0.06 + cos(pos.y * 1.7 + uTime * 1.5) * 0.06;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  }
`;

export const surgeFrag = /* glsl */ `
  uniform float uTime;
  uniform vec3 uColor;
  varying vec2 vUv;
  void main() {
    float ripple = 0.5 + 0.5 * sin(vUv.x * 60.0 + uTime * 3.0) * sin(vUv.y * 60.0 - uTime * 2.0);
    vec3 col = mix(uColor * 0.6, uColor, ripple);
    gl_FragColor = vec4(col, 0.82);
  }
`;
